import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAdd,
  faEdit,
  faSearch,
  faTrash,
  faFolder,
  faFolderOpen,
  faLeaf,
} from "@fortawesome/free-solid-svg-icons";
import { Modal, Box, Typography, Button } from "@mui/material";
import "../screenstyles/Product.css";
import "../screenstyles/CollectionManagement.css";
import { ApiError } from "../api/apiError";
import { filterOptionsApi } from "../api/filterOptionsApi";

import type {
  FilterOption,
  FilterValue,
} from "../types/filter-options.types";


type FilterOptionForm = {
  id: string;
  name: string;
  nameAr: string;
  isActive: boolean;
  sortOrder: number;
};

type FilterValueForm = {
  id: string;

  name: string;
  nameAr: string;

  parentId: string | null;
  isLeaf: boolean;

  isActive: boolean;
  sortOrder: number;
};

const emptyOption: FilterOptionForm = {
  id: "",
  name: "",
  nameAr: "",
  isActive: true,
  sortOrder: 1,
};

const emptyValue: FilterValueForm = {
  id: "",
  name: "",
  nameAr: "",

  parentId: null,
  isLeaf: true,

  isActive: true,
  sortOrder: 1,
};

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/\+/g, "plus")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function getFilterOptionErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  if (!(error instanceof ApiError)) {
    return fallbackMessage;
  }

  switch (error.code) {
    case "FILTER_OPTION_ALREADY_EXISTS":
      return "يوجد فلتر آخر بنفس الـ ID.";

    case "FILTER_OPTION_NAME_ALREADY_EXISTS":
      return "يوجد فلتر آخر بنفس الاسم الإنجليزي.";

    case "FILTER_OPTION_NOT_FOUND":
      return "الفلتر غير موجود.";

    case "FILTER_OPTION_ALREADY_INACTIVE":
      return "هذا الفلتر غير مفعل بالفعل.";

    case "FORBIDDEN":
      return "لا تملك الصلاحية المطلوبة لتنفيذ العملية.";

    case "VALIDATION_ERROR":
      return "بيانات الفلتر غير صحيحة. تحقق من الحقول.";

    default:
      return error.message || fallbackMessage;
  }
}

function getFilterValueErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  if (!(error instanceof ApiError)) {
    return fallbackMessage;
  }

  switch (error.code) {
    case "FILTER_OPTION_NOT_FOUND":
      return "الفلتر غير موجود.";

    case "FILTER_OPTION_INACTIVE":
      return "لا يمكن إضافة قيمة إلى فلتر غير مفعل.";

    case "FILTER_VALUE_ALREADY_EXISTS":
      return "توجد قيمة أخرى بنفس الـ ID داخل هذا الفلتر.";

    case "FILTER_VALUE_NAME_ALREADY_EXISTS":
      return "توجد قيمة أخرى بنفس الاسم الإنجليزي داخل هذا الفلتر.";

    case "FILTER_VALUE_NOT_FOUND":
      return "قيمة الفلتر غير موجودة.";

    case "FILTER_VALUE_ALREADY_INACTIVE":
      return "هذه القيمة غير مفعلة بالفعل.";

      case "FILTER_VALUE_PARENT_NOT_FOUND":
  return "القيمة الأب غير موجودة.";

case "FILTER_VALUE_PARENT_INACTIVE":
  return "لا يمكن وضع القيمة تحت أب غير مفعل.";

case "FILTER_VALUE_PARENT_IS_LEAF":
  return "القيمة المختارة كأب هي قيمة نهائية ولا يمكن أن تحتوي قيمًا فرعية.";

case "FILTER_VALUE_SELF_PARENT":
  return "لا يمكن جعل القيمة أبًا لنفسها.";

case "FILTER_VALUE_CIRCULAR_PARENT":
  return "لا يمكن إنشاء علاقة دائرية بين قيم الفلتر.";

case "FILTER_VALUE_HAS_CHILDREN":
  return "هذه القيمة تحتوي على قيم فرعية. يجب نقل القيم الفرعية منها أولًا.";

    case "FORBIDDEN":
      return "لا تملك الصلاحية المطلوبة لإدارة قيم الفلاتر.";

    case "VALIDATION_ERROR":
      return "بيانات قيمة الفلتر غير صحيحة. تحقق من الحقول.";

    default:
      return error.message || fallbackMessage;
  }
}

const FilterOptions: React.FC = () => {
  const [options, setOptions] = useState<FilterOption[]>([]);
  const [values, setValues] = useState<FilterValue[]>([]);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [valuesLoading, setValuesLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");

  const [optionFormOpen, setOptionFormOpen] = useState(false);
  const [valueFormOpen, setValueFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<{
    type: "option" | "value";
    id: string;
  } | null>(null);

  const [editingOptionId, setEditingOptionId] = useState<string | null>(null);
  const [editingValueId, setEditingValueId] = useState<string | null>(null);

  const [optionForm, setOptionForm] = useState<FilterOptionForm>(emptyOption);
  const [valueForm, setValueForm] =   useState<FilterValueForm>(emptyValue);
  const [savingOption, setSavingOption] = useState(false);
  const [savingValue, setSavingValue] = useState(false);
  const fetchOptions = async () => {
  setLoading(true);

  try {
    const result =
      await filterOptionsApi.listOptions({
        sort: "sortOrder_asc",
        limit: 100,
      });

    const items = result.items;

    setOptions(items);

    setSelectedOptionId((currentSelectedId) => {
      if (
        currentSelectedId &&
        items.some(
          (option) =>
            option.id === currentSelectedId,
        )
      ) {
        return currentSelectedId;
      }

      return items[0]?.id ?? null;
    });
  } catch (error) {
    console.error(
      "Error fetching filter options from API:",
      error,
    );

    toast.error(
      "حدث خطأ أثناء تحميل الفلاتر من الخادم",
    );
  } finally {
    setLoading(false);
  }
};

const fetchValues = async (
  optionId: string,
) => {
  setValuesLoading(true);

  try {
    const result =
      await filterOptionsApi.listValues(
        optionId,
        {
          sort: "sortOrder_asc",
          limit: 100,
        },
      );

    setValues(result.items);
  } catch (error) {
    console.error(
      "Error fetching filter values from API:",
      error,
    );

    setValues([]);

    toast.error(
      "حدث خطأ أثناء تحميل قيم الفلتر من الخادم",
    );
  } finally {
    setValuesLoading(false);
  }
};

  useEffect(() => {
    fetchOptions();
  }, []);

  useEffect(() => {
    if (selectedOptionId) {
      fetchValues(selectedOptionId);
    } else {
      setValues([]);
    }
  }, [selectedOptionId]);

  const filteredOptions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    if (!q) return options;

    return options.filter((option) =>
      [option.id, option.name, option.nameAr, String(option.sortOrder)]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [options, searchQuery]);

  const selectedOption =
    options.find((option) => option.id === selectedOptionId) || null;

    type FilterValueRow = {
  value: FilterValue;
  depth: number;
};

const hierarchicalValues =
  useMemo<FilterValueRow[]>(() => {
    const childrenMap =
      new Map<
        string | null,
        FilterValue[]
      >();

    for (const value of values) {
      const parentId =
        value.parentId ?? null;

      const current =
        childrenMap.get(parentId) ??
        [];

      current.push(value);

      childrenMap.set(
        parentId,
        current,
      );
    }

    const sortValues = (
      items: FilterValue[],
    ) =>
      [...items].sort(
        (first, second) => {
          const orderDifference =
            first.sortOrder -
            second.sortOrder;

          if (orderDifference !== 0) {
            return orderDifference;
          }

          return first.name.localeCompare(
            second.name,
          );
        },
      );

    const result:
      FilterValueRow[] = [];

    const visited =
      new Set<string>();

    const appendChildren = (
      parentId: string | null,
      depth: number,
    ) => {
      const children =
        sortValues(
          childrenMap.get(
            parentId,
          ) ?? [],
        );

      for (const child of children) {
        if (
          visited.has(
            child.id,
          )
        ) {
          continue;
        }

        visited.add(
          child.id,
        );

        result.push({
          value: child,
          depth,
        });

        appendChildren(
          child.id,
          depth + 1,
        );
      }
    };

    appendChildren(
      null,
      0,
    );

    /*
     * حماية للبيانات القديمة أو
     * القيم التي Parent الخاص بها
     * غير موجود.
     */
    for (
      const value of
      sortValues(values)
    ) {
      if (
        !visited.has(
          value.id,
        )
      ) {
        result.push({
          value,
          depth: 0,
        });
      }
    }

    return result;
  }, [values]);

  const openAddOption = () => {
    setEditingOptionId(null);
    setOptionForm({
      ...emptyOption,
      sortOrder: options.length + 1,
    });
    setOptionFormOpen(true);
  };

const openEditOption = (
  option: FilterOption,
) => {
  setEditingOptionId(option.id);

  setOptionForm({
    id: option.id,
    name: option.name,
    nameAr: option.nameAr,
    isActive: option.isActive,
    sortOrder: option.sortOrder,
  });

  setOptionFormOpen(true);
};

  const closeOptionForm = () => {
    setEditingOptionId(null);
    setOptionForm(emptyOption);
    setOptionFormOpen(false);
  };

  const handleSaveOption = async () => {
  const normalizedId =
    editingOptionId ||
    slugify(optionForm.id || optionForm.name);

  const normalizedName =
    optionForm.name.trim();

  const normalizedNameAr =
    optionForm.nameAr.trim();

  const normalizedSortOrder =
    Number(optionForm.sortOrder);

  if (
    !normalizedId ||
    !normalizedName ||
    !normalizedNameAr
  ) {
    toast.error(
      "يرجى إدخال ID والاسم العربي والإنجليزي",
    );
    return;
  }

  if (
    !Number.isInteger(normalizedSortOrder) ||
    normalizedSortOrder < 0
  ) {
    toast.error(
      "الترتيب يجب أن يكون عددًا صحيحًا يساوي صفرًا أو أكثر",
    );
    return;
  }

  try {
    setSavingOption(true);

    if (editingOptionId) {
      await filterOptionsApi.updateOption(
        editingOptionId,
        {
          name: normalizedName,
          nameAr: normalizedNameAr,
          isActive: Boolean(
            optionForm.isActive,
          ),
          sortOrder:
            normalizedSortOrder,
        },
      );
    } else {
      await filterOptionsApi.createOption({
        id: normalizedId,
        name: normalizedName,
        nameAr: normalizedNameAr,
        isActive: Boolean(
          optionForm.isActive,
        ),
        sortOrder:
          normalizedSortOrder,
      });
    }

    toast.success(
      editingOptionId
        ? "تم تعديل الفلتر"
        : "تمت إضافة الفلتر",
      {
        className:
          "products-toast-success",
      },
    );

    closeOptionForm();

    await fetchOptions();

    setSelectedOptionId(normalizedId);
  } catch (error) {
    console.error(
      "Error saving filter option through API:",
      error,
    );

    toast.error(
      getFilterOptionErrorMessage(
        error,
        "حدث خطأ أثناء حفظ الفلتر",
      ),
    );
  } finally {
    setSavingOption(false);
  }
};

const openAddValue = () => {
  if (!selectedOptionId) {
    toast.error("اختر فلتر أولاً");
    return;
  }

  if (!selectedOption?.isActive) {
    toast.error(
      "يجب تفعيل الفلتر قبل إضافة قيمة جديدة",
    );
    return;
  }

  setEditingValueId(null);

  setValueForm({
    ...emptyValue,
    sortOrder: values.length + 1,
  });

  setValueFormOpen(true);
};

 const openEditValue = (
  value: FilterValue,
) => {
  setEditingValueId(value.id);

setValueForm({
  id: value.id,

  name: value.name,
  nameAr: value.nameAr,

  parentId:
    value.parentId ??
    null,

  isLeaf:
    value.isLeaf,

  isActive:
    value.isActive,

  sortOrder:
    value.sortOrder,
});

  setValueFormOpen(true);
};

  const closeValueForm = () => {
    setEditingValueId(null);
    setValueForm(emptyValue);
    setValueFormOpen(false);
  };

const handleSaveValue = async () => {
  if (!selectedOptionId) {
    toast.error("اختر فلتر أولاً");
    return;
  }

  const normalizedId =
    editingValueId ||
    slugify(
      valueForm.id ||
      valueForm.name,
    );

  const normalizedName =
    valueForm.name.trim();

  const normalizedNameAr =
    valueForm.nameAr.trim();

  const normalizedSortOrder =
    Number(valueForm.sortOrder);

  if (
    !normalizedId ||
    !normalizedName ||
    !normalizedNameAr
  ) {
    toast.error(
      "يرجى إدخال ID والاسم العربي والإنجليزي",
    );
    return;
  }

  if (
    !Number.isInteger(normalizedSortOrder) ||
    normalizedSortOrder < 0
  ) {
    toast.error(
      "الترتيب يجب أن يكون عددًا صحيحًا يساوي صفرًا أو أكثر",
    );
    return;
  }

  try {
    setSavingValue(true);

    if (editingValueId) {
      console.log(
  "UPDATE FILTER VALUE:",
  {
    optionId:
      selectedOptionId,

    valueId:
      editingValueId,

    parentId:
      valueForm.parentId,

    isLeaf:
      valueForm.isLeaf,

    fullForm:
      valueForm,
  },
);
  await filterOptionsApi.updateValue(
  selectedOptionId,
  editingValueId,
  {
    name:
      normalizedName,

    nameAr:
      normalizedNameAr,

    parentId:
      valueForm.parentId,

    isLeaf:
      Boolean(
        valueForm.isLeaf,
      ),

    isActive:
      Boolean(
        valueForm.isActive,
      ),

    sortOrder:
      normalizedSortOrder,
  },
);
    } else {
     await filterOptionsApi.createValue(
  selectedOptionId,
  {
    id:
      normalizedId,

    name:
      normalizedName,

    nameAr:
      normalizedNameAr,

    parentId:
      valueForm.parentId,

    isLeaf:
      Boolean(
        valueForm.isLeaf,
      ),

    isActive:
      Boolean(
        valueForm.isActive,
      ),

    sortOrder:
      normalizedSortOrder,
  },
);
    }

    toast.success(
      editingValueId
        ? "تم تعديل القيمة"
        : "تمت إضافة القيمة",
      {
        className:
          "products-toast-success",
      },
    );

    closeValueForm();

    await fetchValues(
      selectedOptionId,
    );

    await fetchOptions();
  } catch (error) {
    console.error(
      "Error saving filter value through API:",
      error,
    );

    toast.error(
      getFilterValueErrorMessage(
        error,
        "حدث خطأ أثناء حفظ قيمة الفلتر",
      ),
    );
  } finally {
    setSavingValue(false);
  }
};

  const openDelete = (target: { type: "option" | "value"; id: string }) => {
    setDeleteTarget(target);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteOpen(false);
  };

const handleDelete = async () => {
  if (!deleteTarget) return;

  try {
    if (
      deleteTarget.type === "option"
    ) {
      setSavingOption(true);

      const result =
        await filterOptionsApi.deleteOption(
          deleteTarget.id,
        );

      toast.success(
        result.valuesCount > 0
          ? `تم تعطيل الفلتر، ويحتوي على ${result.valuesCount} قيمة`
          : "تم تعطيل الفلتر",
        {
          className:
            "products-toast-success",
        },
      );

      closeDelete();

      await fetchOptions();

      return;
    }

    if (
      deleteTarget.type === "value" &&
      selectedOptionId
    ) {
      setSavingValue(true);

      await filterOptionsApi.deleteValue(
        selectedOptionId,
        deleteTarget.id,
      );

      toast.success(
        "تم تعطيل قيمة الفلتر",
        {
          className:
            "products-toast-success",
        },
      );

      closeDelete();

      await fetchValues(
        selectedOptionId,
      );

      await fetchOptions();
    }
  } catch (error) {
    console.error(
      "Error disabling filter item through API:",
      error,
    );

    if (
      deleteTarget.type === "option"
    ) {
      toast.error(
        getFilterOptionErrorMessage(
          error,
          "حدث خطأ أثناء تعطيل الفلتر",
        ),
      );
    } else {
      toast.error(
        getFilterValueErrorMessage(
          error,
          "حدث خطأ أثناء تعطيل قيمة الفلتر",
        ),
      );
    }
  } finally {
    setSavingOption(false);
    setSavingValue(false);
  }
};
  return (
    <div className="products-page collection-page collection-filters-page">
      <div className="products-header collection-header collection-header-filters">
        <h1 className="products-title collection-title">إدارة الفلاتر</h1>

        <div className="products-search-box">
          <FontAwesomeIcon icon={faSearch} className="products-search-icon" />

          <input
            type="text"
            placeholder="البحث في أنواع الفلاتر..."
            className="products-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <button
          type="button"
          className="products-header-icon-btn"
          onClick={openAddOption}
          title="إضافة فلتر"
        >
          <FontAwesomeIcon icon={faAdd} />
        </button>
      </div>

      {loading ? (
        <div className="products-loading">Loading...</div>
      ) : (
        <div className="collection-two-columns">
          <div className="products-table-container collection-side-panel">
            <div className="collection-panel-title">أنواع الفلاتر</div>

            <table className="products-table collection-table collection-compact-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>الاسم</th>
                  <th>الحالة</th>
                  <th>الإجراءات</th>
                </tr>
              </thead>

              <tbody>
                {filteredOptions.map((option) => (
                  <tr
                    key={option.id}
                    className={
                      selectedOptionId === option.id
                        ? "collection-selected-row"
                        : ""
                    }
                    onClick={() => setSelectedOptionId(option.id)}
                  >
                    <td className="products-cell-text">{option.id}</td>
                    <td className="products-cell-text">{option.nameAr}</td>

                    <td className="products-cell-text">
                      <span
                        className={
                          option.isActive
                            ? "collection-badge active"
                            : "collection-badge inactive"
                        }
                      >
                        {option.isActive ? "مفعل" : "غير مفعل"}
                      </span>
                    </td>

                    <td>
                      <div className="products-actions">
                        <button
                          type="button"
                          className="products-action-btn products-edit-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditOption(option);
                          }}
                          title="تعديل"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>

                      <button
  type="button"
  className="products-action-btn products-delete-btn"
  onClick={(e) => {
    e.stopPropagation();

    openDelete({
      type: "option",
      id: option.id,
    });
  }}
  title={
    option.isActive
      ? "تعطيل"
      : "الفلتر معطل"
  }
  disabled={!option.isActive}
>
  <FontAwesomeIcon icon={faTrash} />
</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredOptions.length === 0 && (
              <div className="products-empty-state">لا توجد فلاتر.</div>
            )}
          </div>

          <div className="products-table-container collection-main-panel">
            <div className="collection-panel-header">
              <div className="collection-panel-title">
                قيم الفلتر: {selectedOption?.nameAr || "اختر فلتر"}
              </div>

             <button
  type="button"
  className="collection-mini-btn"
  onClick={openAddValue}
  disabled={
    !selectedOption ||
    !selectedOption.isActive
  }
  title={
    !selectedOption
      ? "اختر فلتر أولاً"
      : !selectedOption.isActive
        ? "يجب تفعيل الفلتر أولاً"
        : "إضافة قيمة"
  }
>
  + إضافة قيمة
</button>
            </div>

            {valuesLoading ? (
              <div className="products-loading">Loading...</div>
            ) : (
    <div className="filter-tree">
  {hierarchicalValues.map(
    ({ value, depth }) => {
      const hasChildren =
        values.some(
          (child) =>
            child.parentId ===
            value.id,
        );

      return (
        <div
          key={value.id}
          className={
            value.isActive
              ? "filter-tree-node"
              : "filter-tree-node filter-tree-node-inactive"
          }
          style={{
            marginRight:
              `${depth * 34}px`,
          }}
        >
          <div className="filter-tree-node-line">

            {/* خط الشجرة */}
            {depth > 0 && (
              <span className="filter-tree-branch">
                └─
              </span>
            )}

            {/* الأيقونة */}
            <span className="filter-tree-icon">
              <FontAwesomeIcon
                icon={
                  hasChildren
                    ? faFolderOpen
                    : faLeaf
                }
              />
            </span>

            {/* الاسم */}
            <div className="filter-tree-info">
              <div className="filter-tree-name">
                {value.nameAr ||
                  value.name}

                {hasChildren && (
                  <span className="filter-tree-parent-badge">
                    فرع
                  </span>
                )}
              </div>

              <div className="filter-tree-meta">
                <span>
                  ID: {value.id}
                </span>

                <span>
                  EN: {value.name}
                </span>

                <span>
                  الترتيب: {value.sortOrder}
                </span>

                <span
                  className={
                    value.isActive
                      ? "collection-badge active"
                      : "collection-badge inactive"
                  }
                >
                  {value.isActive
                    ? "مفعل"
                    : "غير مفعل"}
                </span>
              </div>
            </div>

            {/* الإجراءات */}
            <div className="filter-tree-actions">
              <button
                type="button"
                className="products-action-btn products-edit-btn"
                onClick={() =>
                  openEditValue(value)
                }
                title="تعديل"
              >
                <FontAwesomeIcon
                  icon={faEdit}
                />
              </button>

              <button
                type="button"
                className="products-action-btn products-delete-btn"
                onClick={() =>
                  openDelete({
                    type: "value",
                    id: value.id,
                  })
                }
                title={
                  value.isActive
                    ? "تعطيل"
                    : "القيمة معطلة"
                }
                disabled={
                  !value.isActive
                }
              >
                <FontAwesomeIcon
                  icon={faTrash}
                />
              </button>
            </div>
          </div>
        </div>
      );
    },
  )}
</div>
            )}

            {selectedOptionId && values.length === 0 && !valuesLoading && (
              <div className="products-empty-state">
                لا توجد قيم لهذا الفلتر.
              </div>
            )}
          </div>
        </div>
      )}

      <Modal
        open={optionFormOpen}
        onClose={closeOptionForm}
        className="products-modal"
      >
        <Box className="products-modal-card collection-form-card">
          <Typography className="products-modal-title">
            {editingOptionId ? "تعديل فلتر" : "إضافة فلتر"}
          </Typography>

          <div className="collection-form-grid">
            <label>
              ID
              <input
                disabled={Boolean(editingOptionId)}
                value={optionForm.id}
                onChange={(e) =>
                  setOptionForm({
                    ...optionForm,
                    id: slugify(e.target.value),
                  })
                }
                placeholder="camera_type"
              />
            </label>

            <label>
              الاسم EN
              <input
                value={optionForm.name}
                onChange={(e) =>
                  setOptionForm({
                    ...optionForm,
                    name: e.target.value,
                    id: editingOptionId
                      ? optionForm.id
                      : slugify(e.target.value),
                  })
                }
                placeholder="Camera Type"
              />
            </label>

            <label>
              الاسم AR
              <input
                value={optionForm.nameAr}
                onChange={(e) =>
                  setOptionForm({
                    ...optionForm,
                    nameAr: e.target.value,
                  })
                }
                placeholder="نوع الكاميرا"
              />
            </label>

            <label>
              الترتيب
              <input
                type="number"
                value={optionForm.sortOrder}
                onChange={(e) =>
                  setOptionForm({
                    ...optionForm,
                    sortOrder: Number(e.target.value),
                  })
                }
              />
            </label>

            <label className="collection-checkbox-label">
              <input
                type="checkbox"
                checked={optionForm.isActive}
                onChange={(e) =>
                  setOptionForm({
                    ...optionForm,
                    isActive: e.target.checked,
                  })
                }
              />
              مفعل
            </label>
          </div>

          <Box className="products-modal-actions">
           <Button
  className="collection-save-btn"
  onClick={handleSaveOption}
  disabled={savingOption}
>
  {savingOption
    ? "جاري الحفظ..."
    : "حفظ"}
</Button>

            <Button
              className="products-modal-cancel-btn"
              onClick={closeOptionForm}
            >
              إلغاء
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={valueFormOpen}
        onClose={closeValueForm}
        className="products-modal"
      >
        <Box className="products-modal-card collection-form-card">
          <Typography className="products-modal-title">
            {editingValueId ? "تعديل قيمة" : "إضافة قيمة"}
          </Typography>

          <div className="collection-form-grid">
            <label>
              ID
              <input
                disabled={Boolean(editingValueId)}
                value={valueForm.id}
                onChange={(e) =>
                  setValueForm({
                    ...valueForm,
                    id: slugify(e.target.value),
                  })
                }
                placeholder="dome"
              />
            </label>

            <label>
              الاسم EN
              <input
                value={valueForm.name}
                onChange={(e) =>
                  setValueForm({
                    ...valueForm,
                    name: e.target.value,
                    id: editingValueId
                      ? valueForm.id
                      : slugify(e.target.value),
                  })
                }
                placeholder="Dome"
              />
            </label>

            <label>
              الاسم AR
              <input
                value={valueForm.nameAr}
                onChange={(e) =>
                  setValueForm({
                    ...valueForm,
                    nameAr: e.target.value,
                  })
                }
                placeholder="دوم"
              />
            </label>


<label>
  الأب
  <select
    value={
      valueForm.parentId ??
      ""
    }
    onChange={(e) =>
      setValueForm({
        ...valueForm,
        parentId:
          e.target.value ||
          null,
      })
    }
  >
    <option value="">
      Root - بدون أب
    </option>

   {values
  .filter(
    (value) =>
      value.id !==
        editingValueId &&
      value.isActive,
  )
  .map((value) => (
    <option
      key={value.id}
      value={value.id}
    >
      {value.nameAr ||
        value.name}
      {" — "}
      {value.id}
    </option>
  ))}
  </select>
</label>

<label>
  نوع القيمة

  <select
    value={
      valueForm.isLeaf
        ? "leaf"
        : "parent"
    }
    onChange={(e) =>
      setValueForm({
        ...valueForm,
        isLeaf:
          e.target.value ===
          "leaf",
      })
    }
  >
    <option value="leaf">
      قيمة نهائية Leaf
    </option>

    <option value="parent">
      مجموعة / Parent
    </option>
  </select>
</label>

            <label>
              الترتيب
              <input
                type="number"
                value={valueForm.sortOrder}
                onChange={(e) =>
                  setValueForm({
                    ...valueForm,
                    sortOrder: Number(e.target.value),
                  })
                }
              />
            </label>

            <label className="collection-checkbox-label">
              <input
                type="checkbox"
                checked={valueForm.isActive}
                onChange={(e) =>
                  setValueForm({
                    ...valueForm,
                    isActive: e.target.checked,
                  })
                }
              />
              مفعل
            </label>
          </div>

          <Box className="products-modal-actions">
           <Button
  className="collection-save-btn"
  onClick={handleSaveValue}
  disabled={savingValue}
>
  {savingValue
    ? "جاري الحفظ..."
    : "حفظ"}
</Button>
            <Button
              className="products-modal-cancel-btn"
              onClick={closeValueForm}
            >
              إلغاء
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={deleteOpen} onClose={closeDelete} className="products-modal">
        <Box className="products-modal-card">
     <Typography className="products-modal-title">
  {deleteTarget?.type === "option"
    ? "هل أنت متأكد من تعطيل هذا الفلتر؟"
    : "هل أنت متأكد من تعطيل هذه القيمة؟"}
</Typography>

          <Box className="products-modal-actions">
         <Button
  className="products-modal-delete-btn"
  onClick={handleDelete}
  disabled={
    savingOption ||
    savingValue
  }
>
  {savingOption || savingValue
    ? "جاري التعطيل..."
    : "نعم، عطّل"}
</Button>

            <Button className="products-modal-cancel-btn" onClick={closeDelete}>
              إلغاء
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default FilterOptions;